import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, Download, X, Upload, Search, ArrowRight } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { toast } from "sonner";

const TAGS = [
  "Headshot", "LinkedIn", "Instagram", "Facebook", "Website", "Blog",
  "Email Signature", "Speaker Bio", "Lifestyle", "Professional", "Casual", "Seasonal", "Other",
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [favOnly, setFavOnly] = useState(false);
  const [activeTags, setActiveTags] = useState([]);
  const [search, setSearch] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const r = await api.get("/gallery");
    setItems(r.data.images || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mark Step 6 complete when user views gallery with at least one image
  useEffect(() => {
    if (items.length >= 1) {
      api.post("/progress/gallery-viewed").catch(() => {});
    }
  }, [items.length]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (favOnly && !it.is_favourite) return false;
      if (activeTags.length > 0 && !activeTags.some((t) => (it.tags || []).includes(t))) return false;
      if (search) {
        const blob = `${it.file_name} ${(it.tags || []).join(" ")} ${it.prompt_used || ""}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, favOnly, activeTags, search]);

  const toggleTag = (t) => {
    setActiveTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const toggleFav = async (it) => {
    await api.patch(`/gallery/${it.id}`, { is_favourite: !it.is_favourite });
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_favourite: !it.is_favourite } : x)));
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/gallery/${it.id}`);
    setItems((prev) => prev.filter((x) => x.id !== it.id));
    if (lightbox?.id === it.id) setLightbox(null);
  };

  const downloadOne = async (it) => {
    try {
      const r = await fetch(`${API}/gallery/${it.id}/file?token=${encodeURIComponent(getToken() || "")}`);
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = it.file_name || `brandshot-${it.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadAll = async () => {
    if (filtered.length === 0) return;
    toast.message(`Downloading ${filtered.length} image${filtered.length === 1 ? "" : "s"}…`);
    for (const it of filtered) {
      // eslint-disable-next-line no-await-in-loop
      await downloadOne(it);
    }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto text-[#52525b]">Loading gallery…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="gallery-empty">
        <span className="eyebrow">Your gallery</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-3">Your Brand Photo Gallery</h1>
        <div className="mt-10 bsp-card p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#F5E6E0] flex items-center justify-center mx-auto">
            <Upload className="w-6 h-6 text-[#D4A853]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-2xl text-[#1A1F3D] mt-5">No images in your gallery yet.</h3>
          <p className="text-[#52525b] mt-2">Your gallery is empty. Complete the photo system to create your first brand photos!</p>
          <Link to="/app/step1" className="btn-primary mt-6 inline-flex">
            Start at Step 1 <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="gallery-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="eyebrow">Your gallery</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-3">Your Brand Photo Gallery</h1>
          <p className="text-[#52525b] mt-2">{items.length} image{items.length === 1 ? "" : "s"} · {items.filter((i) => i.is_favourite).length} favourite{items.filter((i) => i.is_favourite).length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={downloadAll} className="btn-primary" data-testid="download-all">
          <Download className="w-4 h-4" /> Download All
        </button>
      </div>

      {/* Filters */}
      <div className="mt-8 bsp-card p-5 space-y-4" data-testid="filters">
        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div className="relative md:max-w-sm flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
            <input
              placeholder="Search by tag, file name or prompt…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bsp-input pl-9"
              data-testid="gallery-search"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1A1F3D]">
            <input type="checkbox" checked={favOnly} onChange={(e) => setFavOnly(e.target.checked)} className="accent-[#D4A853]" data-testid="fav-filter" />
            Favourites only
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => {
            const active = activeTags.includes(t);
            return (
              <button
                key={t}
                onClick={() => toggleTag(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-[#1A1F3D] border-[#1A1F3D] text-white"
                    : "bg-white border-[#e5e7eb] text-[#52525b] hover:border-[#D4A853]"
                }`}
                data-testid={`filter-${t}`}
              >
                {t}
              </button>
            );
          })}
          {(activeTags.length > 0 || favOnly) && (
            <button onClick={() => { setActiveTags([]); setFavOnly(false); }} className="text-xs text-[#D4A853] underline ml-2">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Masonry */}
      {filtered.length === 0 ? (
        <div className="mt-10 text-[#52525b] text-sm">No images match those filters.</div>
      ) : (
        <div className="mt-8 columns-2 md:columns-3 lg:columns-4 gap-4" data-testid="masonry-grid">
          {filtered.map((it) => (
            <MasonryItem
              key={it.id}
              item={it}
              onOpen={() => setLightbox(it)}
              onToggleFav={() => toggleFav(it)}
              onDownload={() => downloadOne(it)}
              onDelete={() => remove(it)}
            />
          ))}
        </div>
      )}

      {lightbox && (
        <Lightbox
          item={lightbox}
          onClose={() => setLightbox(null)}
          onToggleFav={() => toggleFav(lightbox)}
          onDownload={() => downloadOne(lightbox)}
          onDelete={() => remove(lightbox)}
        />
      )}
    </div>
  );
}

const MasonryItem = ({ item, onOpen, onToggleFav, onDownload, onDelete }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/gallery/${item.id}/file?token=${encodeURIComponent(getToken() || "")}`);
        if (!r.ok) return;
        const b = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch (err) {
        console.error("Failed to load gallery image:", err);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [item.id]);

  return (
    <div className="mb-4 break-inside-avoid bsp-card overflow-hidden group relative" data-testid={`item-${item.id}`}>
      <button onClick={onOpen} className="block w-full text-left">
        <div className="bg-[#F5E6E0]">
          {src ? (
            <img src={src} alt={item.file_name} className="w-full block" />
          ) : (
            <div className="aspect-square" />
          )}
        </div>
      </button>
      <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onToggleFav} className={`w-9 h-9 rounded-full flex items-center justify-center shadow ${item.is_favourite ? "bg-[#D4A853] text-[#1A1F3D]" : "bg-white/95 text-[#1A1F3D]"}`} data-testid={`fav-${item.id}`} aria-label="Toggle favourite">
          <Star className={`w-4 h-4 ${item.is_favourite ? "fill-current" : ""}`} />
        </button>
        <button onClick={onDownload} className="w-9 h-9 rounded-full bg-white/95 text-[#1A1F3D] flex items-center justify-center shadow" data-testid={`dl-${item.id}`} aria-label="Download">
          <Download className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="w-9 h-9 rounded-full bg-white/95 text-red-600 flex items-center justify-center shadow" data-testid={`del-${item.id}`} aria-label="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {item.is_favourite && (
        <div className="absolute top-2 left-2 bg-[#D4A853] text-[#1A1F3D] text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">★ Favourite</div>
      )}
      {item.tags?.length > 0 && (
        <div className="p-3 flex flex-wrap gap-1">
          {item.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F5E6E0] text-[#1A1F3D] rounded-full font-medium">{t}</span>
          ))}
          {item.tags.length > 4 && <span className="text-[10px] text-[#52525b]">+{item.tags.length - 4}</span>}
        </div>
      )}
    </div>
  );
};

const Lightbox = ({ item, onClose, onToggleFav, onDownload, onDelete }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      const r = await fetch(`${API}/gallery/${item.id}/file?token=${encodeURIComponent(getToken() || "")}`);
      const b = await r.blob();
      if (cancelled) return;
      url = URL.createObjectURL(b);
      setSrc(url);
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [item.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="lightbox">
      <div className="relative max-w-6xl w-full grid lg:grid-cols-3 gap-0 max-h-[92vh] bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="lg:col-span-2 bg-black flex items-center justify-center min-h-[40vh]">
          {src && <img src={src} alt={item.file_name} className="max-h-[92vh] w-full object-contain" />}
        </div>
        <div className="p-6 md:p-8 overflow-y-auto scrollbar-clean">
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 text-[#1A1F3D] flex items-center justify-center shadow" aria-label="Close" data-testid="lightbox-close">
            <X className="w-5 h-5" />
          </button>
          <span className="eyebrow">Brand photo</span>
          <h2 className="font-display text-2xl text-[#1A1F3D] mt-2">{item.file_name}</h2>
          {item.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {item.tags.map((t) => (
                <span key={t} className="text-xs px-2.5 py-1 bg-[#F5E6E0] text-[#1A1F3D] rounded-full font-medium">{t}</span>
              ))}
            </div>
          )}
          {item.prompt_used && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#52525b]">Prompt used</div>
              <div className="mt-1 text-sm text-[#2D2D2D]">{item.prompt_used}</div>
            </div>
          )}
          <div className="mt-6 grid grid-cols-3 gap-2">
            <button onClick={onToggleFav} className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 ${item.is_favourite ? "bg-[#D4A853] text-[#1A1F3D]" : "bg-[#F5E6E0] text-[#1A1F3D] hover:bg-[#ecd6cd]"}`}>
              <Star className={`w-4 h-4 ${item.is_favourite ? "fill-current" : ""}`} /> Favourite
            </button>
            <button onClick={onDownload} className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 bg-[#1A1F3D] text-white">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={onDelete} className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 border border-[#e5e7eb] text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
